import { useEffect, useState, useCallback, useRef } from "react";
import { useUserStore } from "../stores/userStore";
import { socket } from "../utils/socket";
import {
	forceUnlockReport,
	getActiveReportLock,
	lockReport,
	unlockReport,
	ReportLock,
	LockReportPayload,
	UnlockReportPayload,
} from "../services/api-server/report_locks";

export const useReportLock = (
	reportId?: string,
	reportType?: string,
	onForceUnlock?: () => void,
	initialLock?: ReportLock | null,
	onLockChange?: (lock: ReportLock | null) => void
) => {
	const [lock, setLock] = useState<ReportLock | null>(initialLock ?? null);
	const [loading, setLoading] = useState(false);
	const currentUser = useUserStore((state) => state.user);
	const onForceUnlockRef = useRef(onForceUnlock);
	const onLockChangeRef = useRef(onLockChange);

	useEffect(() => {
		onForceUnlockRef.current = onForceUnlock;
	}, [onForceUnlock]);

	useEffect(() => {
		onLockChangeRef.current = onLockChange;
	}, [onLockChange]);

	const currentUserEmail = currentUser?.email?.toLowerCase();

	const isLockedByMe = !!(
		lock?.locked_by_email &&
		currentUserEmail &&
		lock.locked_by_email.toLowerCase() === currentUserEmail
	);

	const isLocked = !!lock?.locked_by_email;

	const handleLockResponse = useCallback((payload: ReportLock | null) => {
		if (!payload?.locked_by_email) {
			setLock(null);
			onLockChangeRef.current?.(null);
			return;
		}
		setLock(payload);
		onLockChangeRef.current?.(payload);
	}, []);

	const acquire = useCallback(async () => {
		if (!reportId || !reportType || !currentUserEmail) return;
		setLoading(true);
		try {
			const payload: LockReportPayload = {
				report_id: reportId,
				report_type: reportType,
				user: {
					email: currentUser.email,
					name: currentUser.name,
				},
			};
			const result = await lockReport(payload);
			handleLockResponse(result);
			return result;
		} catch (error: any) {
			// If already locked by someone else, fetch the active lock so the UI can show it
			const activeLock = await getActiveReportLock(reportId, reportType);
			handleLockResponse(activeLock);
			throw error;
		} finally {
			setLoading(false);
		}
	}, [reportId, reportType, currentUser, currentUserEmail, handleLockResponse]);

	const release = useCallback(async () => {
		if (!reportId || !reportType || !currentUserEmail) return;
		setLoading(true);
		try {
			const payload: UnlockReportPayload = {
				report_id: reportId,
				report_type: reportType,
				user_email: currentUserEmail,
			};
			await unlockReport(payload);
			setLock(null);
		} finally {
			setLoading(false);
		}
	}, [reportId, reportType, currentUserEmail]);

	const forceUnlock = useCallback(async () => {
		if (!reportId || !reportType) return;
		setLoading(true);
		try {
			await forceUnlockReport({
				report_id: reportId,
				report_type: reportType,
			});
			setLock(null);
		} finally {
			setLoading(false);
		}
	}, [reportId, reportType]);

	const refresh = useCallback(async () => {
		if (!reportId || !reportType) return;
		const activeLock = await getActiveReportLock(reportId, reportType);
		handleLockResponse(activeLock);
	}, [reportId, reportType, handleLockResponse]);

	useEffect(() => {
		if (!reportId || !reportType || !currentUserEmail) return;

		// Fetch any existing lock once on mount (view mode), but do not acquire one.
		refresh();

		const eventName = `report_lock:${reportId}`;
		const onLockEvent = (payload: any) => {
			if (payload?.event === "unlocked" && payload?.report_id === reportId) {
				setLock(null);
				onLockChangeRef.current?.(null);
			} else if (
				payload?.event === "force_unlocked" &&
				payload?.report_id === reportId
			) {
				setLock(null);
				onLockChangeRef.current?.(null);
				onForceUnlockRef.current?.();
			} else if (payload?.report_id === reportId) {
				setLock(payload);
				onLockChangeRef.current?.(payload);
			}
		};
		socket.on(eventName, onLockEvent);

		return () => {
			socket.off(eventName, onLockEvent);
			// Intentionally do NOT unlock here. The lock must persist when the
			// drawer closes or the page refreshes, and is only released via
			// handleExitEditMode, force unlock, or the report leaving draft.
		};
	}, [reportId, reportType, currentUserEmail, refresh]);

	return {
		lock,
		loading,
		isLocked,
		isLockedByMe,
		acquire,
		release,
		forceUnlock,
		refresh,
	};
};
